import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { MapPin } from 'lucide-react'

import { ResponseDetailPage } from '@/systems/surveys/features/response-detail/components/ResponseDetailPage'
import {
  taskDetailQueryOptions,
  taskIssuesQueryOptions,
} from '@/systems/surveys/features/response-detail/hooks/useTasks'

// HOD survey-response viewer. Mirrors the admin route under /entities, but
// back-navigates to the HOD site detail page (and renders a HOD-appropriate
// breadcrumb so HOD users don't hit admin-only links). Parent /hod already
// gates the subtree to role=hod, so no extra beforeLoad needed here.
export const Route = createFileRoute('/hod/sites/$siteId_/response-details')({
  validateSearch: (search: Record<string, unknown>) => ({
    taskId: (search['taskId'] as string) ?? '',
  }),
  loaderDeps: ({ search }) => ({ taskId: search.taskId }),
  loader: ({ context: { queryClient }, deps: { taskId } }) => {
    if (taskId) {
      void queryClient.prefetchQuery(taskDetailQueryOptions(taskId))
      void queryClient.prefetchQuery(taskIssuesQueryOptions(taskId))
    }
  },
  component: HodResponseDetailRoute,
})

function HodResponseDetailRoute() {
  const { siteId } = Route.useParams()
  const { taskId } = Route.useSearch()
  const navigate = useNavigate()

  return (
    <ResponseDetailPage
      taskId={taskId}
      onBack={() => navigate({ to: '/hod/sites/$siteId', params: { siteId } })}
      breadcrumb={(task) => (
        <nav className="text-muted-text mb-2 inline-flex items-center gap-1.5 text-xs">
          <Link
            to="/hod/sites"
            className="text-ink-3 hover:bg-line-2 hover:text-ink inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium transition"
          >
            <MapPin size={12} />
            Sites
          </Link>
          <span className="text-muted-text-2">›</span>
          <Link
            to="/hod/sites/$siteId"
            params={{ siteId }}
            className="text-ink-3 hover:bg-line-2 hover:text-ink inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium transition"
          >
            {task.site.name}
          </Link>
          <span className="text-muted-text-2">›</span>
          <span className="text-ink font-semibold">Survey Response</span>
        </nav>
      )}
    />
  )
}
